import React, { useEffect, useState } from 'react';
import {
  Popover, Box, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Chip, Stack,
} from '@mui/material';
import { Warehouse } from '@mui/icons-material';
import inventarioInformes from '../../../services/inventarioInformes';

interface StockRow {
  nroUbicacion: number;
  nombreUbicacion: string;
  cantidadDeposito: number;
  cantidadAsignada: number;
  disponibleDeposito: number;
}

interface Props {
  codigoArticulo: string;
  descripcion: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const FacturaPOSStockPopover: React.FC<Props> = ({
  codigoArticulo,
  descripcion,
  anchorEl,
  onClose,
}) => {
  const [rows, setRows] = useState<StockRow[]>([]);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !codigoArticulo) return;
    let cancelled = false;

    const doFetch = async () => {
      setLoading(true);
      setRows([]);
      try {
        const res = await inventarioInformes.getStockPorDeposito({
          codigoArticulo,
          pageSize: 50,
        });
        const data: any[] = (res?.data ?? []).filter(
          (r: any) => (r.codigoArticulo ?? '').trim() === codigoArticulo.trim(),
        );
        if (!cancelled) setRows(data);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    doFetch();
    return () => {
      cancelled = true;
    };
  }, [open, codigoArticulo]);

  const totalDisponible = rows.reduce((acc, r) => acc + (r.disponibleDeposito ?? 0), 0);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { p: 2, minWidth: 360, maxWidth: 500, borderRadius: 2 } }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Warehouse fontSize="small" color="action" />
        <Box>
          <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
            {descripcion}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {codigoArticulo}
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Sin información de stock disponible
        </Typography>
      ) : (
        <>
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>Depósito</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                  Cantidad
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5 }}>
                  Asignado
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.5, color: 'success.main' }}
                >
                  Disponible
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.nroUbicacion} hover>
                  <TableCell sx={{ fontSize: '0.8rem', py: 0.5 }}>
                    {r.nombreUbicacion || `Depósito ${r.nroUbicacion}`}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.5 }}>
                    {r.cantidadDeposito}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.8rem', py: 0.5, color: 'text.secondary' }}>
                    {r.cantidadAsignada}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.8rem',
                      py: 0.5,
                      fontWeight: 600,
                      color: r.disponibleDeposito > 0 ? 'success.main' : 'error.main',
                    }}
                  >
                    {r.disponibleDeposito}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="flex-end">
            <Chip
              label={`Total disponible: ${totalDisponible}`}
              size="small"
              color={totalDisponible > 0 ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>
        </>
      )}
    </Popover>
  );
};

export default FacturaPOSStockPopover;
